import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext } from 'react';
import { settingsApi } from '@/features/settings/api/settingsApi';
import { settingsKeys } from '@/features/settings/hooks/useSettings';
import type { FeatureFlag } from '@/features/settings/types';
import { useAuth } from '@/providers/AuthProvider';

interface ProductFeaturesContextValue {
  flags: FeatureFlag[];
  isLoading: boolean;
}

const ProductFeaturesContext = createContext<ProductFeaturesContextValue>({
  flags: [],
  isLoading: true,
});

export function ProductFeaturesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: settingsKeys.features(),
    queryFn: () => settingsApi.getFeatures(),
    staleTime: 30_000,
    retry: 1,
    enabled: isAuthenticated,
  });

  console.log(
    '[ProductFeatures] data:',
    data?.map((f) => `${f.id}=${f.enabled}`),
    'isLoading:',
    isLoading
  );

  // No useMemo — React Query already handles referential stability via
  // structural sharing. When data changes, `data` is a new reference,
  // which makes this a new context value, triggering consumer re-renders.
  // When data hasn't changed, `data` is the same reference, so the
  // context value object is different but React's context diffing is
  // shallow — it will still re-render consumers. This is acceptable
  // because the provider only re-renders on auth or query state changes.
  return (
    <ProductFeaturesContext.Provider value={{ flags: data ?? [], isLoading }}>
      {children}
    </ProductFeaturesContext.Provider>
  );
}

/** Check a single feature flag */
export function useProductFeature(flagId: string) {
  const { flags, isLoading } = useContext(ProductFeaturesContext);
  const flag = flags.find((f) => f.id === flagId);
  // status comes back on the same payload and answers a different question from
  // enabled: whether the product works, rather than whether it is switched on.
  return { enabled: flag?.enabled ?? false, status: flag?.status, isLoading };
}

/** Access all feature flags */
export function useProductFeatures() {
  const { flags, isLoading } = useContext(ProductFeaturesContext);
  const isFeatureEnabled = (flagId: string) => flags.find((f) => f.id === flagId)?.enabled ?? false;
  return { isFeatureEnabled, isLoading };
}
